import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantsApi, type Tenant } from '@/api/tenantsApi'
import { networksApi, type InstanceNetwork } from '@/api/networksApi'
import { useAuth } from '@/providers/AuthProvider'

const TENANT_STORAGE_KEY = 'cgt_selected_tenant'

type TenantContextValue = {
  /**
   * The instance record, shown as a static label. Networks are not selectable:
   * one deployment hosts one network, so there is nothing to switch between.
   */
  instanceNetwork: InstanceNetwork | null
  /** Every tenant the user may act as (already scoped server-side). */
  tenants: Tenant[]
  selectedTenantId: string | null
  selectedTenant: Tenant | null
  /** Switch the acting tenant; flushes the query cache. */
  setSelectedTenantId: (id: string) => void
  isLoading: boolean
  /**
   * True once the tenant question is settled: signed out, the fetch errored,
   * or the list arrived and a selection exists (or there is nothing to
   * select). Layout holds rendering until this is true so authFetch's
   * X-Tenant-Id header is never silently omitted on a cold load. Derived from
   * isSuccess/isError, NOT isLoading — a disabled query reports
   * isLoading: false, which would falsely read as resolved.
   */
  isTenantResolved: boolean
  error: string | null
}

const TenantContext = createContext<TenantContextValue | null>(null)

export function TenantProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()

  // Only fetch once signed in. TenantProvider is mounted at the app root (above
  // the router), so an ungated query fires at startup before login: it 401s and,
  // because this single observer never remounts during SPA navigation, never
  // refetches (hence the "tenants only load after a refresh" bug). Gating on auth
  // lets the enabled false->true flip at login trigger the fetch automatically.
  const {
    data,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useQuery({
    queryKey: ['tenants'],
    queryFn: tenantsApi.list,
    enabled: isAuthenticated,
  })

  const { data: instanceNetwork } = useQuery({
    queryKey: ['instance-network'],
    queryFn: networksApi.current,
    enabled: isAuthenticated,
  })

  const [selectedTenantId, setSelectedTenantIdState] = useState<string | null>(() =>
    localStorage.getItem(TENANT_STORAGE_KEY),
  )

  const tenants = useMemo(() => data ?? [], [data])

  // Signing out clears the persisted tenant (clearAuth), but this provider sits
  // above the router and never unmounts — so without this its state would keep
  // the previous session's tenant id. That stale id makes isTenantResolved true
  // straight away (no Layout hold) while the sync effect below sees the id as
  // still valid and never re-persists it, leaving every request without an
  // X-Tenant-Id header until a full page reload. Drop the selection with the
  // session so the next sign-in re-resolves it from scratch.
  useEffect(() => {
    if (isAuthenticated) return
    setSelectedTenantIdState(null)
  }, [isAuthenticated])

  // Once tenants load, default to the stored selection if still valid, else the
  // first tenant.
  useEffect(() => {
    // Never select while signed out: the tenant list stays in the query cache
    // after logout, so without this the effect would immediately re-select
    // (and re-persist) a tenant for the session that just ended, undoing the
    // reset above and skipping the Layout hold on the next sign-in.
    if (!isAuthenticated) return
    if (tenants.length === 0) {
      // A signed-in user with zero visible tenants must not keep a stale
      // selection: authFetch would silently ride that X-Tenant-Id on every
      // request even though the user cannot act as it anymore.
      if (isSuccess && selectedTenantId !== null) {
        setSelectedTenantIdState(null)
        localStorage.removeItem(TENANT_STORAGE_KEY)
      }
      return
    }
    const current = tenants.find((t) => t.id === selectedTenantId)
    if (!current) {
      const first = tenants[0]
      setSelectedTenantIdState(first.id)
      localStorage.setItem(TENANT_STORAGE_KEY, first.id)
      return
    }
    // authFetch reads the STORED tenant, not this state, so repair any drift
    // between the two rather than trusting state alone.
    if (localStorage.getItem(TENANT_STORAGE_KEY) !== current.id) {
      localStorage.setItem(TENANT_STORAGE_KEY, current.id)
    }
  }, [tenants, selectedTenantId, isSuccess, isAuthenticated])

  // useCallback so it can be a declared dependency of the context memo below.
  // As a plain function it was rebuilt every render while the memo listed only
  // its captured values, which happens to be safe today and stops being safe the
  // moment this closes over something the memo does not track.
  const setSelectedTenantId = useCallback(
    (id: string) => {
      if (id === selectedTenantId) return
      setSelectedTenantIdState(id)
      localStorage.setItem(TENANT_STORAGE_KEY, id)
      // Tenant-scoped data is cached under keys that mostly do NOT include the
      // tenant id — a switch must flush everything or the next page serves the
      // previous tenant's rows from cache. Central removal beats auditing every
      // inline query key in the app.
      //
      // REMOVE, not invalidate: invalidation keeps the cached rows and refetches
      // in the background, so a component re-rendering in between paints the
      // PREVIOUS tenant's data for a frame. Removing guarantees a loading state
      // instead, which is the only honest thing to show mid-switch.
      //
      // The three exclusions are tenant-independent and must survive: dropping
      // ['tenants'] flips isTenantResolved false and flashes Layout's
      // full-screen spinner on every switch, ['instance-network'] is the
      // instance record, identical for every tenant, and ['user-preferences']
      // holds the signed-in person's own UI settings — their table column
      // layouts describe how they like to READ a table, which does not change
      // when they switch tenant. Dropping it would refetch identical data and
      // briefly leave every table without its saved layout.
      queryClient.removeQueries({
        predicate: (q) =>
          q.queryKey[0] !== 'tenants' &&
          q.queryKey[0] !== 'instance-network' &&
          q.queryKey[0] !== 'user-preferences',
      })
    },
    [selectedTenantId, queryClient],
  )

  const isTenantResolved =
    !isAuthenticated ||
    isError ||
    (isSuccess && (selectedTenantId !== null || tenants.length === 0))

  const value = useMemo<TenantContextValue>(
    () => ({
      instanceNetwork: instanceNetwork ?? null,
      tenants,
      selectedTenantId,
      selectedTenant: tenants.find((t) => t.id === selectedTenantId) ?? null,
      setSelectedTenantId,
      isLoading,
      isTenantResolved,
      error: error instanceof Error ? error.message : null,
    }),
    [
      instanceNetwork,
      tenants,
      selectedTenantId,
      setSelectedTenantId,
      isLoading,
      isTenantResolved,
      error,
    ],
  )

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext)
  if (!ctx) throw new Error('useTenant must be used within a TenantProvider')
  return ctx
}

/**
 * The tenant context, or null outside a TenantProvider. For components that
 * must also render standalone — the form renderer is mounted with no providers
 * in its SSR smoke tests, and must keep rendering there.
 */
export function useOptionalTenant(): TenantContextValue | null {
  return useContext(TenantContext)
}
